import { useEffect, useRef, useState } from 'react';

interface DataPoint {
  index: number;
  label: string;
  col1: number;
  col2?: number;
  col3?: number;
  col4?: number;
  col5?: number;
  col6?: number;
  col7?: number;
  col8?: number;
  col9?: number;
  col10?: number;
}

interface AnimatedBarGraphProps {
  data: DataPoint[];
  backgroundColor: string;
  animationSpeed: number;
  shouldDraw: boolean;
  onRecordingComplete?: (blob: Blob) => void;
  axisLabels?: { label: string; col1: string; col2?: string; col3?: string; col4?: string; col5?: string; col6?: string; col7?: string; col8?: string; col9?: string; col10?: string; } | null;
  barWidth: number;
  barSpacing: number;
  showBarLabels: boolean;
  shiftLargestLeft: boolean;
  labelFontSize: number;
  captureVideo: boolean;
  showWatermark: boolean;
}

export function AnimatedBarGraph({
  data,
  backgroundColor,
  animationSpeed,
  shouldDraw,
  onRecordingComplete,
  axisLabels,
  barWidth = 40,
  barSpacing = 20,
  showBarLabels = true,
  shiftLargestLeft = false,
  labelFontSize = 14,
  captureVideo = false,
  showWatermark = true
}: AnimatedBarGraphProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const currentFrameRef = useRef<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const lastFrameTimeRef = useRef<number>(0);
  const [isRecording, setIsRecording] = useState(false);

  // Define a color palette for the bars
  const barColors = [
    '#007bff', // Blue
    '#28a745', // Green
    '#dc3545', // Red
    '#ffc107', // Yellow
    '#17a2b8', // Cyan
    '#6610f2', // Purple
    '#fd7e14', // Orange
    '#20c997', // Teal
    '#e83e8c', // Pink
    '#6f42c1'  // Indigo
  ];

  const startRecording = async () => {
    if (!canvasRef.current) return;

    // Check for supported MIME types in order of preference for Twitter/X compatibility
    const mimeTypes = [
      'video/mp4;codecs=avc1.42E01E', // H.264 Baseline Profile
      'video/mp4;codecs=avc1.4D401E', // H.264 Main Profile
      'video/mp4;codecs=avc1.64001E', // H.264 High Profile
      'video/mp4;codecs=h264',        // Generic H.264
      'video/mp4',                    // Generic MP4
      'video/webm;codecs=h264',       // WebM with H.264 (rare but possible)
      'video/webm;codecs=vp9',        // VP9 as fallback
      'video/webm;codecs=vp8',        // VP8 as fallback
      'video/webm'                    // Generic WebM
    ];

    console.log('Checking supported MIME types for Twitter/X compatibility:');
    mimeTypes.forEach(mimeType => {
      const isSupported = MediaRecorder.isTypeSupported(mimeType);
      console.log(`${mimeType}: ${isSupported ? 'SUPPORTED' : 'NOT SUPPORTED'}`);
    });

    let selectedMimeType = null;
    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        selectedMimeType = mimeType;
        console.log('Selected MIME type for recording:', mimeType);
        break;
      }
    }

    if (!selectedMimeType) {
      console.error('No supported video format found');
      return;
    }

    try {
      const stream = canvasRef.current.captureStream(30); // 30 FPS
      
      // Try to configure the MediaRecorder with specific codec settings
      const options: MediaRecorderOptions = {
        mimeType: selectedMimeType
      };

      // Add additional options for better H.264 compatibility
      if (selectedMimeType.includes('mp4') || selectedMimeType.includes('h264') || selectedMimeType.includes('avc1')) {
        console.log('Using MP4/H.264 configuration for Twitter/X compatibility');
      }

      const mediaRecorder = new MediaRecorder(stream, options);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('Received video chunk:', event.data.size, 'bytes, type:', event.data.type);
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Check the actual type of the first chunk to determine the real format
        let actualType = 'video/mp4';
        if (recordedChunksRef.current.length > 0) {
          const firstChunk = recordedChunksRef.current[0];
          actualType = firstChunk.type || selectedMimeType;
          console.log('First chunk type:', firstChunk.type, 'Selected MIME type:', selectedMimeType);
        }
        
        // Determine the correct file extension and blob type
        let fileExtension = 'mp4';
        let blobType = 'video/mp4';
        
        // Force MP4 container for better Twitter/X compatibility
        if (selectedMimeType.includes('mp4') || selectedMimeType.includes('h264') || selectedMimeType.includes('avc1')) {
          fileExtension = 'mp4';
          blobType = 'video/mp4';
          console.log('Using MP4 container for Twitter/X compatibility');
        } else if (actualType.includes('webm') || selectedMimeType.includes('webm')) {
          fileExtension = 'webm';
          blobType = 'video/webm';
          console.log('Falling back to WebM container');
        }
        
        const blob = new Blob(recordedChunksRef.current, {
          type: blobType
        });
        console.log('Video blob created:', blob.size, 'bytes, type:', blobType, 'actual chunks type:', actualType);
        console.log('File will be saved as:', `animated-bar-graph.${fileExtension}`);
        recordedChunksRef.current = [];
        if (onRecordingComplete) {
          onRecordingComplete(blob);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Main animation effect
  useEffect(() => {
    if (!shouldDraw || !canvasRef.current || data.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = 800;
    canvas.height = 600;

    // Get column names from first row
    const columnNames = Object.keys(data[0]).filter(key => key.startsWith('col'));
    const numColumns = columnNames.length;
    const totalWidth = (barWidth + barSpacing) * numColumns - barSpacing;
    const startX = (canvas.width - totalWidth) / 2;
    const maxHeight = canvas.height - 100; // Leave space for labels
    const bottomY = canvas.height - 50; // Space for column names

    // Get labels from axisLabels if available
    const labels = columnNames.map(col => {
      const colIndex = parseInt(col.replace('col', ''));
      return axisLabels?.[`col${colIndex}` as keyof typeof axisLabels] || col;
    });

    // Find max value for scaling
    const maxValue = Math.max(...data.map(point => 
      Math.max(...columnNames.map(col => point[col as keyof DataPoint] as number))
    ));

    const drawFrame = (frameIndex: number) => {
      // Clear canvas
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw watermark if enabled
      if (showWatermark) {
        ctx.save();
        ctx.globalAlpha = 0.1; // Make watermark semi-transparent
        
        // Determine watermark color based on background
        const isDarkBackground = backgroundColor === '#404040' || backgroundColor === 'black';
        const watermarkColor = isDarkBackground ? '#ffffff' : '#000000';
        
        ctx.fillStyle = watermarkColor;
        ctx.font = 'bold 48px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('graffy.ca', canvas.width / 2, canvas.height / 2);
        ctx.restore();
      }

      // Determine text color based on background
      const isDarkBackground = backgroundColor === '#404040' || backgroundColor === 'black';
      const textColor = isDarkBackground ? '#ffffff' : '#000000';

      // Draw vertical axis
      const axisX = startX - 40; // Position axis to the left of the bars
      const axisY = bottomY;
      const axisHeight = maxHeight;

      // Draw main axis line
      ctx.beginPath();
      ctx.strokeStyle = textColor;
      ctx.lineWidth = 2;
      ctx.moveTo(axisX, axisY);
      ctx.lineTo(axisX, axisY - axisHeight);
      ctx.stroke();

      // Draw tick marks and labels
      const numTicks = 5; // Number of tick marks
      const tickSpacing = axisHeight / (numTicks - 1);
      
      for (let i = 0; i < numTicks; i++) {
        const y = axisY - (i * tickSpacing);
        const value = (i * maxValue) / (numTicks - 1);
        
        // Draw tick mark
        ctx.beginPath();
        ctx.moveTo(axisX, y);
        ctx.lineTo(axisX - 5, y);
        ctx.stroke();

        // Draw value label
        ctx.fillStyle = textColor;
        ctx.font = `${labelFontSize}px Arial`;
        ctx.textAlign = 'right';
        ctx.fillText(value.toFixed(0), axisX - 8, y + 4);
      }

      // Draw current label in top right corner
      ctx.fillStyle = textColor;
      ctx.font = `${labelFontSize + 6}px Arial`; // Make the frame label slightly larger
      ctx.textAlign = 'right';
      ctx.fillText(data[frameIndex].label, canvas.width - 20, 30);

      // Get current frame's data and sort columns if needed
      const currentData = data[frameIndex];
      let columnsToDraw = columnNames;
      let labelsToDraw = labels;
      
      if (shiftLargestLeft) {
        // Create array of {column, value, label, colorIndex} pairs
        const columnValues = columnNames.map((col, idx) => ({
          column: col,
          value: currentData[col as keyof DataPoint] as number,
          label: labels[idx],
          colorIndex: idx
        }));
        
        // Sort by value in descending order
        columnValues.sort((a, b) => b.value - a.value);
        
        // Extract sorted column names and labels
        columnsToDraw = columnValues.map(cv => cv.column);
        labelsToDraw = columnValues.map(cv => cv.label);
      }

      // Draw bars
      columnsToDraw.forEach((col, index) => {
        const value = currentData[col as keyof DataPoint] as number;
        const barHeight = (value / maxValue) * maxHeight;
        const x = startX + index * (barWidth + barSpacing);
        
        // Get the original color index for this column
        const originalIndex = columnNames.indexOf(col);
        
        // Draw bar with color from palette using the original index
        ctx.fillStyle = barColors[originalIndex % barColors.length];
        ctx.fillRect(x, bottomY - barHeight, barWidth, barHeight);

        // Draw label from first row
        ctx.fillStyle = textColor;
        ctx.font = `${labelFontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.fillText(labelsToDraw[index], x + barWidth / 2, bottomY + 20);

        // Draw value on top of bar if showBarLabels is true
        if (showBarLabels) {
          ctx.fillStyle = textColor;
          ctx.font = `${labelFontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.fillText(value.toString(), x + barWidth / 2, bottomY - barHeight - 5);
        }
      });
    };

    // Start animation
    const animate = (timestamp: number) => {
      // If we've reached the end of the data, stop the animation
      if (currentFrameRef.current >= data.length) {
        // Draw the final frame multiple times to ensure it's captured
        const finalFrame = data.length - 1;
        let finalFrameCount = 0;
        const maxFinalFrames = 30; // Capture final state for 1 second at 30fps

        const drawFinalFrames = () => {
          if (finalFrameCount < maxFinalFrames) {
            drawFrame(finalFrame);
            finalFrameCount++;
            requestAnimationFrame(drawFinalFrames);
          } else if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            // Add a small delay after drawing all final frames before stopping
            setTimeout(() => {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
              }
            }, 1000); // 1 second delay to ensure all frames are captured
          }
        };

        drawFinalFrames();
        return;
      }

      // Convert animationSpeed (0.01-0.2) to a delay between 100ms and 2000ms
      const frameDelay = 2000 - (animationSpeed * 9500);
      const targetTime = lastFrameTimeRef.current + frameDelay;
      
      // Only draw if enough time has passed
      if (timestamp >= targetTime) {
        drawFrame(currentFrameRef.current);
        currentFrameRef.current++;
        lastFrameTimeRef.current = timestamp;
      }

      // Always request the next frame, regardless of whether we drew or not
      if (shouldDraw) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    // Start animation
    currentFrameRef.current = 0;
    lastFrameTimeRef.current = 0;
    if (shouldDraw) {
      // Start recording if captureVideo is true
      if (captureVideo) {
        startRecording();
      }
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
      // Only stop recording if we're unmounting or shouldDraw becomes false
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording' && !shouldDraw) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [shouldDraw, data, backgroundColor, animationSpeed, axisLabels, barWidth, barSpacing, showBarLabels, shiftLargestLeft, labelFontSize, captureVideo, showWatermark, onRecordingComplete]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '800px',
        height: '600px',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
      }}
    />
  );
} 